/**
 * [WTD-1826] Redeems the single-use `launch_code` DMP puts in the launch URL, so this application
 * obtains its first access token over HTTPS instead of asking the old socket for one.
 *
 * Replaces the WebSocket `getToken` route, which would hand the session's access token to anyone
 * who connected quoting a known session id. Only the bootstrap changes: renewal stays with DMP,
 * which keeps broadcasting `renewToken` on the session channel.
 */

import { LocalStorageKeys } from '@curvebeamai/web-connect-service';
import { AUTH_DATA_KEY, getAccessTokenFromStorage } from './access-validation.service';
import { fetchWithTimeout, getApiBaseUrl } from './http.utils';

/**
 * What the library says it stores auth data under, or undefined if it no longer exposes it.
 *
 * Read through optional chaining rather than directly, because this runs at module scope in a file
 * the session bootstrap imports. A direct `LocalStorageKeys.authDataKey` throws a TypeError at
 * IMPORT time if the object ever goes away, and that is a white screen for the whole viewer rather
 * than the degraded-but-running state the check below exists to report - the guard cannot fire if
 * the line above it is what breaks.
 *
 * Kept separate from the key actually used, which matters more than it looks. Collapsing the two
 * into `LocalStorageKeys?.authDataKey ?? AUTH_DATA_KEY` makes the comparison below read
 * AUTH_DATA_KEY !== AUTH_DATA_KEY whenever the library value is missing, so the one case worth
 * reporting is the one case reported silently.
 *
 * The repo has no typecheck in its build or CI - rspack strips types with swc and nothing runs tsc
 * - so this runtime check is the only thing standing between the two constants.
 */
const libraryAuthDataKey = (LocalStorageKeys as { authDataKey?: string } | undefined)?.authDataKey;

if (libraryAuthDataKey !== AUTH_DATA_KEY) {
  console.error(
    '[LaunchCode] web-connect-service stores auth under',
    libraryAuthDataKey ?? '(not exposed)',
    'but this application reads',
    AUTH_DATA_KEY,
    '- the launch token will still be read here, but renewToken broadcasts will be written where this application does not look, so the session will end when the first token expires.'
  );
}

/**
 * The key this file writes to.
 *
 * Falls back to this application's own constant rather than to undefined, which would send every
 * write to a key literally named "undefined" - the outcome when the library keeps LocalStorageKeys
 * but renames the property, which throws nothing and is the quieter of the two ways this can break.
 *
 * Every viewer tab on this origin shares this key, and `getAccessTokenFromStorage()` re-reads it on
 * every request to build the Authorization header, which is why this application holds its own copy
 * of the name rather than asking the library each time.
 */
const authDataKey: string = libraryAuthDataKey ?? AUTH_DATA_KEY;

type LaunchExchangeResponse = { accessToken?: string; sessionId?: string };

/**
 * Exchanges the code for this session's access token and seeds the shared auth store.
 *
 * Posted straight to dmp-backend, which issued the code. The Viewer already reaches dmp-backend for
 * everything else, so unlike Autometrics it needs no relay through a backend of its own.
 *
 * Returns the access token, or null if the code was rejected - it is single-use and valid only for
 * seconds, so a reload or a re-opened tab legitimately fails here and falls back to the stored
 * token.
 */
export async function exchangeLaunchCode(code: string, sessionId: string): Promise<string | null> {
  // Reuses the shared resolver rather than reading backendBaseUrl directly, so the trailing-slash
  // handling lives in one place.
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    console.error(
      '[LaunchCode] backendBaseUrl is not configured; cannot exchange the launch code.'
    );
    return null;
  }

  try {
    // No Authorization header, and none is possible: the code stands in for the token this
    // application does not have yet. getAuthHeaders() would send an empty set here anyway.
    const response = await fetchWithTimeout(`${baseUrl}/Account/launchExchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      // Not escalated to an error. A spent code answers 400, and that is an ordinary consequence of
      // reloading - the caller decides what a failed exchange means.
      //
      // Logged all the same, because the null returned below is the same null returned for a 500,
      // for a CORS rejection and for a timeout. Without the status, a support report of "it says to
      // open the viewer from DMP" names none of them, and the only other line on this path is
      // validateSession reporting that it ended up with no token.
      console.warn(`[LaunchCode] exchange refused: ${response.status} ${response.statusText}`);
      return null;
    }

    const body = (await response.json()) as
      | { data?: LaunchExchangeResponse }
      | LaunchExchangeResponse;
    // dmp-backend wraps responses in an envelope; tolerate both shapes rather than assuming one.
    const data: LaunchExchangeResponse =
      (body as { data?: LaunchExchangeResponse })?.data ?? (body as LaunchExchangeResponse);

    if (!data?.accessToken) {
      // A 200 carrying no token. Not something the backend should ever send, so it is worth being
      // able to tell apart from the refusals above rather than reading as one.
      console.warn('[LaunchCode] exchange returned 200 with no access token in the body');
      return null;
    }

    // The exchange posts only the code, so dmp-backend answers with the session that CODE belongs
    // to and never sees the session_id in this URL - it cannot check the two agree, so this is the
    // only place that can.
    //
    // A mismatch can only be a bug or a code used against the wrong launch URL, and letting it
    // through fails twice over, neither failure pointing at the cause. The channel path is built
    // from the URL's session, so the viewer would subscribe to /session/{userId}/{urlSession} while
    // the token belongs elsewhere: connected, and receiving nothing - no renewToken, no logout.
    // Then the next load reads the stored record as foreign and clears the token, which looks like
    // a spontaneous logout minutes later.
    //
    // Refused rather than corrected, because there is no version of this that is safe to guess at.
    // null puts it on the no-credential path, which lands on invalid-source - "open this from DMP"
    // being the right advice for a launch URL whose code belongs to another session.
    if (data.sessionId && data.sessionId !== sessionId) {
      console.error(
        `[LaunchCode] exchange returned session ${data.sessionId} for a launch into ${sessionId}`
      );
      return null;
    }

    // Seeded into the store web-connect-service reads from, so renewToken handling and the token
    // validation interval keep working untouched - and so sibling tabs of this session find it.
    //
    // Replaced rather than merged, deliberately. The library's own setAuthData writes exactly
    // { accessToken, sessionId } and replaces the key too, and its TokenViewModel reader keeps only
    // those two fields - so there is nothing else under this key to preserve. DMP stores
    // refreshToken, idToken and expireDate alongside them, which is what managesOwnToken exists
    // for, but localStorage is partitioned by origin and DMP is a different one. Merging here would
    // preserve junk from a stale record that the library's reader discards anyway.
    localStorage.setItem(
      authDataKey,
      JSON.stringify({ accessToken: data.accessToken, sessionId: data.sessionId || sessionId })
    );

    return data.accessToken;
  } catch (e) {
    // Network level rather than a refusal: a timeout, DNS, a failed CORS preflight, or the request
    // being aborted. The error was previously not even bound, so this class of failure left no
    // trace anywhere - which is the one it is least possible to guess at after the fact.
    console.warn('[LaunchCode] exchange could not be completed:', e);
    return null;
  }
}

/**
 * The token from an earlier exchange, if this origin already has one.
 *
 * Covers two cases that look the same from here and are both legitimate: a reload, whose code was
 * spent on the first load, and a sibling viewer tab of the same session that was never given a code
 * of its own. Reuses the Viewer's own reader so there is one definition of how that key is parsed.
 */
export function storedAccessToken(): string | null {
  return getAccessTokenFromStorage() || null;
}
